import Link from 'next/link';

import { OptimizelyFeatureFlags } from '@tgg/common-types';
import { getTestId, isSupportCentre } from '@tgg/util';

import { useFlagsContext, useTggTheme } from '../../contexts';
import { Palette } from '../../theme';
import { Chip } from '../Chip';
import { Icon } from '../Icon';

import {
    StyledText,
    StyledListItem,
    StyledContainer,
    StyledHorizontalRule,
    StyledSubMenuText,
    StyledLinkContainer,
    StyledChipSpan,
} from './NavigationItem.styled';
import { NavigationItemProperties } from './NavigationItem.types';

/**
 * Use the `NavigationItem` component as an Item in the Navigation.
 */
export function NavigationItem({
    isSubMenuAlternativeItem = false,
    text,
    leftIconName,
    divider = true,
    onClick,
    href,
    tabIndex,
    caseSensitive = false,
    testId,
    itemTestId,
    chipText,
    chipColor,
}: NavigationItemProperties) {
    let redirectWithFeatureFlag = null;
    let textWithFeatureFlag = null;

    const {
        flags: {
            [OptimizelyFeatureFlags.MULTIPLE_ADD_ONS_ENABLED]: {
                enabled: isMultipleAddonsEnabled,
            },
            [OptimizelyFeatureFlags.REBRANDING]: { enabled: isRebrand },
        },
    } = useFlagsContext();

    const { theme } = useTggTheme();

    caseSensitive = isRebrand ? true : caseSensitive;

    if (
        (text === 'Manage extras' || text === 'Manage add-ons') &&
        isMultipleAddonsEnabled
    ) {
        redirectWithFeatureFlag = '/your-membership/manage-add-ons/';
        textWithFeatureFlag = 'Manage add-ons';
    }

    if (href && leftIconName) {
        const isSupportCentreLink = isSupportCentre(href);
        return (
            <StyledListItem>
                <StyledLinkContainer
                    href={href}
                    $leftIconExists
                    {...(onClick && {
                        onClick: () => onClick(),
                    })}
                    tabIndex={tabIndex}
                    rel={isSupportCentreLink ? 'noopener noreferrer' : ''}
                >
                    <Icon
                        name={leftIconName}
                        color={
                            !isRebrand
                                ? (theme.palette.primary.main as Palette)
                                : (theme.palette.tgg.icon.action
                                      .main as Palette)
                        }
                    />

                    {isSubMenuAlternativeItem ? (
                        <StyledSubMenuText
                            $caseSensitive={caseSensitive}
                            $isSubMenuAlternativeItem
                        >
                            {text}
                        </StyledSubMenuText>
                    ) : (
                        <StyledText $caseSensitive={caseSensitive}>
                            {text}
                            <StyledChipSpan>
                                {chipText && chipColor && (
                                    <Chip text={chipText} tint={chipColor} />
                                )}
                            </StyledChipSpan>
                        </StyledText>
                    )}
                </StyledLinkContainer>
                {divider && <StyledHorizontalRule />}
            </StyledListItem>
        );
    }
    if (onClick && leftIconName) {
        return (
            <StyledListItem>
                <StyledContainer
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        onClick();
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            onClick();
                        }
                    }}
                    tabIndex={tabIndex}
                    role="button"
                    aria-pressed={false}
                    data-testid={getTestId(`${testId}-section`)}
                >
                    <Icon
                        name={leftIconName}
                        color={
                            !isRebrand
                                ? (theme.palette.primary.main as Palette)
                                : (theme.palette.tgg.icon.action
                                      .main as Palette)
                        }
                    />

                    <StyledText $caseSensitive={caseSensitive}>
                        {text}
                    </StyledText>

                    <Icon
                        name={isRebrand ? 'chevronRightSmall' : 'chevronRight'}
                        size={22}
                        color={
                            !isRebrand
                                ? (theme.palette.primary.main as Palette)
                                : (theme.palette.tgg.icon.action
                                      .main as Palette)
                        }
                    />
                </StyledContainer>
                {divider && <StyledHorizontalRule />}
            </StyledListItem>
        );
    }
    if (href) {
        return (
            <StyledListItem>
                <Link
                    href={redirectWithFeatureFlag ?? href}
                    passHref
                    legacyBehavior
                >
                    <StyledLinkContainer
                        {...(onClick && {
                            onClick: () => onClick(),
                        })}
                        data-testid={getTestId(`${itemTestId}-item`)}
                    >
                        <StyledSubMenuText $caseSensitive={caseSensitive}>
                            {textWithFeatureFlag ?? text}
                        </StyledSubMenuText>
                    </StyledLinkContainer>
                </Link>
                {divider && <StyledHorizontalRule />}
            </StyledListItem>
        );
    }

    return (
        <StyledListItem>
            <StyledSubMenuText $caseSensitive={caseSensitive}>
                {text}
            </StyledSubMenuText>
            {divider && <StyledHorizontalRule />}
        </StyledListItem>
    );
}
